import type { resolveShiftNavigation } from '@/lib/shapes/shape-text-navigation'
import { verticalStepOffset } from '@/lib/shapes/shape-text-navigation'
import type { ShapeTextCarets } from '../types/shape-text-layout-state'

type KeyInfo = {
  key: string
  ctrlKey: boolean
  metaKey: boolean
  altKey: boolean
  shiftKey: boolean
}

/**
 * Caret-moving keys that collapse a live cross-frame selection when pressed WITHOUT Shift — the
 * same set a browser collapses a native selection on. Shifted variants extend it instead and are
 * handled by `resolveShiftNavigation` well before this is consulted.
 */
export const CARET_NAVIGATION_KEYS = new Set([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
  'PageUp',
  'PageDown',
])

/** Ctrl/Cmd+Z (undo), Ctrl/Cmd+Shift+Z and Ctrl/Cmd+Y (redo). */
export function isUndoRedoCombo(event: KeyInfo): boolean {
  return (
    (event.ctrlKey || event.metaKey) &&
    !event.altKey &&
    (event.key === 'z' || event.key === 'Z' || event.key === 'y' || event.key === 'Y')
  )
}

/** Ctrl/Cmd + `letter` with no Shift or Alt (the B/I/U style toggles). */
export function isModLetter(event: KeyInfo, letter: string): boolean {
  return (
    event.key === letter && (event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey
  )
}

/**
 * The text a key replaces a cross-frame selection with: empty for Backspace/Delete, the produced
 * character otherwise, or null when the key produces no text.
 *
 * `event.key.length === 1` is the standard "produced a character" test (the
 * modifier guard keeps shortcuts like mod+C and mod+B out of it), but Enter and Tab
 * produce text too and fail it — without them a Return over a three-frame selection
 * inserted a paragraph break and deleted nothing.
 */
export function chainReplacementForKey(event: KeyInfo): string | null {
  const noMods = !event.ctrlKey && !event.metaKey

  if (event.key === 'Backspace' || event.key === 'Delete') return ''
  if (event.key === 'Enter' && noMods) return '\n'
  if (event.key === 'Tab' && noMods) return '\t'
  if (event.key.length === 1 && noMods) return event.key

  return null
}

/**
 * Whether a shifted navigation runs out of THIS frame and so has to be resolved by the owner.
 * Anything that stays inside the frame is left to the textarea, which already does it correctly
 * and cheaply.
 *
 * Gates on the resolved sibling list, not on `hasNextInChain`/`hasPrevInChain` alone: those
 * read the raw chain, which can name a member that is not on this spread and so has
 * no live frame. The owner bails on exactly that case, and the caller `preventDefault`s before
 * calling it — so gating any looser would turn the key into a dead press.
 */
export function shiftNavLeavesFrame({
  shiftNav,
  focusLocal,
  carets,
  selectionStart,
  selectionEnd,
  valueLength,
  chainFrameCount,
  hasNextInChain,
  hasPrevInChain,
}: {
  shiftNav: NonNullable<ReturnType<typeof resolveShiftNavigation>>
  focusLocal: number
  carets: ShapeTextCarets
  selectionStart: number
  selectionEnd: number
  valueLength: number
  chainFrameCount: number
  hasNextInChain: boolean
  hasPrevInChain: boolean
}): boolean {
  const forward = shiftNav.direction === 1
  const canLeaveForward = forward
    ? hasNextInChain && chainFrameCount > 1
    : hasPrevInChain && chainFrameCount > 1

  return shiftNav.unit === 'documentBoundary'
    ? chainFrameCount > 1
    : shiftNav.unit === 'lineBoundary'
      ? // Home/End never leave a frame — a visual row belongs to exactly one frame.
        false
      : shiftNav.unit === 'line'
        ? // No further row here, so the step continues into the neighbour.
          verticalStepOffset(carets, focusLocal, shiftNav.direction) == null && canLeaveForward
        : forward
          ? selectionEnd === valueLength && canLeaveForward
          : selectionStart === 0 && canLeaveForward
}
